import { sectionRenderer } from '@theme/section-renderer';

// Adds a recommended product to the cart from the drawer's "you might also
// like" carousel.
//
// Kept deliberately minimal: one add request, one proven section refresh
// (the same sectionRenderer.renderSection call the rest of the theme
// already relies on elsewhere), and a non-blocking header-count update.
// Earlier, more "clever" versions (dispatching the theme's own cart-update
// event, hand-rolling the section morph, pre-checking stock with an extra
// fetch) each fixed one symptom but added a new way to silently fail. This
// version does the least possible and leans on code that's already proven
// to work everywhere else in the theme.
//
// One thing worth noting: on success, the section refresh below re-renders
// this exact button from scratch in its normal state — so there's nothing
// to manually reset afterwards. Only the safety timeout still guards
// against that refresh itself hanging.

async function handleAddClick(event) {
  const button = /** @type {HTMLElement | null} */ (
    event.target instanceof Element ? event.target.closest('[data-cart-recommendation-add]') : null
  );
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  event.preventDefault();

  const variantId = button.dataset.variantId;
  if (!variantId) return;

  button.disabled = true;
  const originalText = button.textContent;

  let settled = false;
  const reset = (text) => {
    if (settled) return;
    settled = true;
    button.disabled = false;
    button.textContent = text;
  };

  // Whatever happens below, the button must not stay stuck forever.
  const safety = setTimeout(() => reset(originalText), 6000);

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            // Hidden marker so cart-drawer-recommendations.liquid can keep
            // basing recommendations on the last "real" add, not on items
            // added from this widget itself.
            properties: { _cart_rec_add: 'true' },
          },
        ],
      }),
    });

    if (!response.ok) {
      const result = await response.json().catch(() => null);
      const soldOut = result?.message?.toLowerCase().includes('verfügbar') ?? false;
      clearTimeout(safety);
      reset(soldOut ? 'Ausverkauft' : 'Fehler – erneut versuchen');
      return;
    }

    await sectionRenderer.renderSection('cart-drawer-section', { cache: false, mode: 'hydration' });
    clearTimeout(safety);
    settled = true;

    // The drawer has to be scrolled down to reach the recommendations widget
    // in the first place, so without this the refreshed item list — now
    // including the product just added — stays scrolled out of view above
    // whatever's currently on screen (the recommendations themselves). Scroll
    // back to the top so the shopper actually sees what they just added.
    const content = document.querySelector('#cart-drawer .cart-drawer__content');
    content?.scrollTo({ top: 0, behavior: 'smooth' });

    // Non-blocking: neither of these is essential to the add itself, so a
    // failure here shouldn't affect it.
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart) return;

        document.querySelectorAll('cart-icon').forEach((el) => {
          if (typeof el.renderCartBubble === 'function') el.renderCartBubble(cart.item_count);
        });

        // The section refresh above re-renders this button from the
        // product's static availability, which has no idea how many are
        // already sitting in the cart — so a variant that's now fully
        // claimed still shows up as addable. Fix that up using the real
        // cart quantity.
        const line = cart.items.find((item) => String(item.variant_id) === String(variantId));
        const quantityInCart = line ? line.quantity : 0;
        const freshButton = document.querySelector(
          `[data-cart-recommendation-add][data-variant-id="${variantId}"]`
        );

        if (freshButton instanceof HTMLButtonElement) {
          const tracked = freshButton.dataset.inventoryTracked === 'true';
          const policy = freshButton.dataset.inventoryPolicy;
          const available = Number(freshButton.dataset.inventoryQuantity);

          if (tracked && policy !== 'continue' && Number.isFinite(available) && quantityInCart >= available) {
            freshButton.disabled = true;
            freshButton.textContent = 'Keine mehr verfügbar';
          }
        }
      })
      .catch(() => {});
  } catch (error) {
    console.error('[cart-drawer-recommendations-add] failed to add product', error);
    clearTimeout(safety);
    reset('Fehler – erneut versuchen');
  }
}

document.addEventListener('click', handleAddClick);
